import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Check as ApproveIcon,
  Send as PostIcon,
  Block as VoidIcon,
  Delete as DeleteIcon,
  RemoveCircleOutline as RemoveLineIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { useAccessControl } from '../../../hooks/useAccessControl';
import { RESOURCES, ACTIONS } from '../../../hooks/usePermissions';
import { ledgerAPI } from '../../../services/api';
import { formatAccountingAmount } from '../../../utils/formatters';
import { DataTable, useAppSnackbar, useConfirmDialog } from '../../common/TableDesign';
import LedgerListPage from '../LedgerListPage';
import LedgerFormDialog from '../LedgerFormDialog';
import { AccountPicker, CustomerPicker } from '../LedgerPickers';
import { useLedgerLookups } from '../Journals';
import { ArStatusChip, fmtDate, getErrorMessage, unwrap } from './ArShared';

const STATUSES = ['DRAFT', 'APPROVED', 'POSTED', 'PARTIALLY_PAID', 'PAID', 'VOID'];

const studentLabel = (inv, look) => {
  if (inv?.customer) return inv.customer.name || `Customer ${inv.customer_id}`;
  return look.customerLabel(inv?.customer_id, `Customer ${inv?.customer_id ?? ''}`);
};

const emptyItem = () => ({ item_name: '', quantity: 1, unit_price: '', revenue_account_id: '' });
const emptyForm = () => ({
  customer_id: '', invoice_date: new Date().toISOString().slice(0, 10), due_date: '',
  items: [emptyItem()],
});

const defaultApi = {
  list: ledgerAPI.getReceivableInvoices,
  get: ledgerAPI.getReceivableInvoice,
  create: ledgerAPI.createReceivableInvoice,
  approve: ledgerAPI.approveReceivableInvoice,
  post: ledgerAPI.postReceivableInvoice,
  void: ledgerAPI.voidReceivableInvoice,
  remove: ledgerAPI.deleteReceivableInvoice,
};

// Student billing: one AR invoice per student, drawn against a revenue
// account per line (see SchoolAccounting::billGroup - fee groups already
// bill this way automatically; this dialog is for one-off / manual billing
// the fee flow doesn't cover). DRAFT -> APPROVED -> POSTED; approve/void
// run as checker, create/post as maker.
//
// `resource`/`api` let FeesBilling.js reuse this exact screen against the
// 'fees' gateway section (same accounts-receivable/invoices DTOs, gated
// under RESOURCES.LEDGER.FEES instead of RECEIVABLES) - see
// config/ledger.php. Defaults match Student Receivables' own usage.
const InvoicesTab = ({ resource = RESOURCES.LEDGER.RECEIVABLES, api = defaultApi }) => {
  const { can } = useAccessControl();
  const canCreate = can(resource, ACTIONS.CREATE);
  const canApprove = can(resource, ACTIONS.APPROVE);
  const canDelete = can(resource, ACTIONS.DELETE);
  const look = useLedgerLookups();
  const { showSnackbar, SnackbarComponent } = useAppSnackbar();
  const { confirm, ConfirmDialogComponent } = useConfirmDialog();

  const [refreshKey, setRefreshKey] = useState(0);
  const [showCreate, setShowCreate] = useState(false);
  const [values, setValues] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [voidDlg, setVoidDlg] = useState({ open: false, item: null, void_reason: '' });

  const reload = () => setRefreshKey((k) => k + 1);

  const setItem = (index, key, value) => {
    setValues((p) => ({ ...p, items: p.items.map((it, i) => (i === index ? { ...it, [key]: value } : it)) }));
  };
  const addItem = () => setValues((p) => ({ ...p, items: [...p.items, emptyItem()] }));
  const removeItem = (index) => setValues((p) => ({ ...p, items: p.items.filter((_, i) => i !== index) }));

  const itemsTotal = values.items.reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0);

  const submitCreate = async () => {
    if (!values.customer_id) {
      showSnackbar('Student is required', 'error');
      return;
    }
    const items = values.items
      .filter((it) => it.item_name || it.unit_price)
      .map((it) => ({
        item_name: it.item_name.trim(),
        quantity: Number(it.quantity) || 1,
        unit_price: Number(it.unit_price),
        revenue_account_id: Number(it.revenue_account_id),
      }));
    if (items.length < 1) {
      showSnackbar('At least one invoice line is required', 'error');
      return;
    }
    if (items.some((it) => !it.item_name || !(it.unit_price > 0) || !it.revenue_account_id)) {
      showSnackbar('Each line needs a description, revenue account and unit price greater than zero', 'error');
      return;
    }
    const payload = {
      customer_id: Number(values.customer_id),
      invoice_date: values.invoice_date,
      items,
    };
    if (values.due_date) payload.due_date = values.due_date;
    try {
      setSaving(true);
      await api.create(payload);
      showSnackbar('Invoice created as DRAFT', 'success');
      setShowCreate(false);
      setValues(emptyForm());
      reload();
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };

  const runWorkflow = (label, pastTense, fn) => {
    confirm({
      title: label,
      message: `Are you sure you want to ${label.toLowerCase()} this invoice?`,
      onConfirm: async () => {
        try {
          await fn();
          showSnackbar(`Invoice ${pastTense} successfully`, 'success');
          reload();
        } catch (error) {
          showSnackbar(getErrorMessage(error), 'error');
        }
      },
    });
  };

  const openDetail = async (row) => {
    try {
      const res = await api.get(row.invoice_id);
      setDetail(unwrap(res));
    } catch (error) {
      showSnackbar(getErrorMessage(error, 'Failed to fetch invoice'), 'error');
    }
  };

  const submitVoid = async () => {
    if ((voidDlg.void_reason?.trim() ?? '').length < 5) {
      showSnackbar('Void reason (min 5 chars) is required', 'error');
      return;
    }
    try {
      await api.void(voidDlg.item.invoice_id, { void_reason: voidDlg.void_reason.trim() });
      showSnackbar('Invoice voided successfully', 'success');
      setVoidDlg({ open: false, item: null, void_reason: '' });
      setDetail(null);
      reload();
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    }
  };

  const detailItems = Array.isArray(detail?.items) ? detail.items : [];

  return (
    <>
      <LedgerListPage
        resource={resource}
        title="Student Invoices"
        subtitle="AR invoices billed to students - one per bill, drawn against a revenue account per line"
        addLabel="New Invoice"
        idKey="invoice_id"
        searchPlaceholder="Search invoice #, student..."
        emptyMessage="No invoices found."
        refreshKey={refreshKey}
        fetchItems={(params) => api.list(params)}
        onAdd={canCreate ? () => { setValues(emptyForm()); setShowCreate(true); } : null}
        filters={[
          { key: 'status', label: 'Status', options: STATUSES.map((s) => ({ label: s, value: s })) },
          { type: 'picker', key: 'customer_id', label: 'Student', picker: CustomerPicker },
          { type: 'daterange', keyFrom: 'start_date', keyTo: 'end_date', label: 'Invoice date' },
          { type: 'numberrange', keyFrom: 'min_amount', keyTo: 'max_amount', label: 'total' },
        ]}
        columns={[
          { label: 'Invoice #', render: (i) => <strong>{i.invoice_number || `INV-${i.invoice_id}`}</strong> },
          { label: 'Student', render: (i) => studentLabel(i, look) },
          { label: 'Invoice Date', render: (i) => fmtDate(i.invoice_date) },
          { label: 'Due Date', render: (i) => fmtDate(i.due_date), hideOnMobile: true },
          { label: 'Total', render: (i) => formatAccountingAmount(i.total_amount ?? i.grand_total), align: 'right' },
          { label: 'Balance Due', render: (i) => formatAccountingAmount(i.balance_due), align: 'right' },
          { label: 'Status', render: (i) => <ArStatusChip status={i.status} /> },
        ]}
        rowActions={(i) => {
          const actions = [{ icon: ViewIcon, title: 'View detail', color: 'default', onClick: () => openDetail(i) }];
          if (i.status === 'DRAFT' && canApprove) {
            actions.push({ icon: ApproveIcon, title: 'Approve', color: 'primary', onClick: () => runWorkflow('Approve', 'approved', () => api.approve(i.invoice_id)) });
          }
          if (i.status === 'APPROVED' && canApprove) {
            actions.push({ icon: PostIcon, title: 'Post', color: 'primary', onClick: () => runWorkflow('Post', 'posted', () => api.post(i.invoice_id)) });
          }
          if (['DRAFT', 'APPROVED', 'POSTED', 'PARTIALLY_PAID'].includes(i.status) && canApprove) {
            actions.push({ icon: VoidIcon, title: 'Void', color: 'error', onClick: () => setVoidDlg({ open: true, item: i, void_reason: '' }) });
          }
          if (i.status === 'DRAFT' && canDelete) {
            actions.push({
              icon: DeleteIcon, title: 'Delete draft', color: 'error',
              onClick: () => confirm({
                title: 'Delete invoice',
                message: `Delete DRAFT invoice ${i.invoice_number || `INV-${i.invoice_id}`}? This cannot be undone.`,
                onConfirm: async () => {
                  try {
                    await api.remove(i.invoice_id);
                    showSnackbar('Invoice deleted', 'success');
                    reload();
                  } catch (error) {
                    showSnackbar(getErrorMessage(error), 'error');
                  }
                },
              }),
            });
          }
          return actions;
        }}
      />

      <Dialog open={showCreate} onClose={() => setShowCreate(false)} maxWidth="lg" fullWidth>
        <DialogTitle>New Student Invoice</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 1, mb: 2 }}>
            <Grid size={{ xs: 12, md: 6, lg: 5 }}>
              <CustomerPicker label="Student" value={values.customer_id} onChange={(id) => setValues((p) => ({ ...p, customer_id: id }))} required />
            </Grid>
            <Grid size={{ xs: 12, md: 6, lg: 5 }}>
              <TextField
                fullWidth size="small" type="date" label="Invoice Date" value={values.invoice_date}
                onChange={(e) => setValues((p) => ({ ...p, invoice_date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6, lg: 5 }}>
              <TextField
                fullWidth size="small" type="date" label="Due Date (optional)" value={values.due_date}
                onChange={(e) => setValues((p) => ({ ...p, due_date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
          <Typography variant="subtitle2" gutterBottom>Invoice lines</Typography>
          {values.items.map((it, i) => (
            <Stack key={i} direction="row" flexWrap="wrap" gap={1.5} alignItems="flex-start" sx={{ mb: 1.5 }}>
              <TextField
                size="small" label="Description" value={it.item_name}
                onChange={(e) => setItem(i, 'item_name', e.target.value)}
                sx={{ flex: '1 1 200px' }}
              />
              <TextField
                size="small" type="number" label="Qty" value={it.quantity}
                onChange={(e) => setItem(i, 'quantity', e.target.value)}
                inputProps={{ min: 1, step: 1 }} sx={{ width: 90 }}
              />
              <TextField
                size="small" type="number" label="Unit Price" value={it.unit_price}
                onChange={(e) => setItem(i, 'unit_price', e.target.value)}
                inputProps={{ min: 0.01, step: 'any' }} sx={{ width: 140 }}
              />
              <Box sx={{ minWidth: 220, flex: '1 1 220px' }}>
                <AccountPicker label="Revenue account" value={it.revenue_account_id} onChange={(id) => setItem(i, 'revenue_account_id', id)} />
              </Box>
              <IconButton size="small" color="error" title="Remove line" onClick={() => removeItem(i)} disabled={values.items.length <= 1}>
                <RemoveLineIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
          <Button size="small" startIcon={<AddIcon />} onClick={addItem}>Add line</Button>
          <Typography variant="body2" sx={{ mt: 1 }} fontWeight={700}>
            Total: {formatAccountingAmount(itemsTotal)}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCreate(false)}>Cancel</Button>
          <Button variant="contained" onClick={submitCreate} disabled={saving}>
            {saving ? 'Saving...' : 'Create Draft'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="lg" fullWidth>
        <DialogTitle>Invoice {detail?.invoice_number || (detail?.invoice_id ? `INV-${detail.invoice_id}` : '')}</DialogTitle>
        <DialogContent>
          {detail && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {studentLabel(detail, look)} - {fmtDate(detail.invoice_date)}
                {detail.due_date ? ` - due ${fmtDate(detail.due_date)}` : ''}
              </Typography>
              <DataTable
                columns={['Description', 'Qty', 'Unit Price', 'Line Total']}
                data={detailItems}
                loading={false}
                emptyMessage="No lines."
                renderRow={(it, i) => (
                  <TableRow key={it.item_id ?? i}>
                    <TableCell>{it.item_name}</TableCell>
                    <TableCell>{it.quantity}</TableCell>
                    <TableCell align="right">{formatAccountingAmount(it.unit_price)}</TableCell>
                    <TableCell align="right">{formatAccountingAmount((Number(it.quantity) || 0) * (Number(it.unit_price) || 0))}</TableCell>
                  </TableRow>
                )}
              />
              <Box display="flex" justifyContent="flex-end" gap={3} sx={{ mt: 1 }}>
                <Typography variant="body2">Total: <strong>{formatAccountingAmount(detail.total_amount ?? detail.grand_total)}</strong></Typography>
                <Typography variant="body2">Balance Due: <strong>{formatAccountingAmount(detail.balance_due)}</strong></Typography>
                <ArStatusChip status={detail.status} />
              </Box>
              {detail.status === 'VOID' && detail.void_reason && (
                <Typography variant="body2" color="error.main" sx={{ mt: 1 }}>
                  Voided: {detail.void_reason}
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetail(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <LedgerFormDialog
        open={voidDlg.open}
        onClose={() => setVoidDlg({ open: false, item: null, void_reason: '' })}
        title={`Void invoice ${voidDlg.item?.invoice_number || (voidDlg.item ? `INV-${voidDlg.item.invoice_id}` : '')}`}
        onSubmit={submitVoid}
        values={voidDlg}
        onChange={(key, value) => setVoidDlg((p) => ({ ...p, [key]: value }))}
        maxWidth="sm"
        fields={[{ key: 'void_reason', label: 'Void Reason (min 5 chars)', type: 'textarea', required: true, md: 12 }]}
      />

      {ConfirmDialogComponent}
      {SnackbarComponent}
    </>
  );
};

export default InvoicesTab;
